import { createContext, useContext, ReactNode } from "react";

export interface AdminCapabilityContextValue {
  capabilities: string[];
  competitionIds: string[] | "ALL";
  /** True until the first GET /admin/me/capabilities response lands (settled or not). */
  loading: boolean;
  /** True if the fetch itself failed. Distinct from "loaded with zero capabilities" —
   *  an empty list on success is a real (if currently unreachable) answer; a failed
   *  fetch tells us nothing, so it must not be read as "no access". */
  error: boolean;
}

const AdminCapabilityContext = createContext<AdminCapabilityContextValue>({
  capabilities: [],
  competitionIds: [],
  loading: true,
  error: false,
});

export function AdminCapabilityProvider({ value, children }: { value: AdminCapabilityContextValue; children: ReactNode }) {
  return <AdminCapabilityContext.Provider value={value}>{children}</AdminCapabilityContext.Provider>;
}

export function useAdminCapabilities() {
  return useContext(AdminCapabilityContext);
}

/**
 * Whether a nav item gated on `capability` should render. Fails open, not closed:
 * while the fetch is in flight, or if it errored, defaults to visible rather than
 * hidden — reaching AdminLayout at all already means the caller is a confirmed
 * admin (gated upstream in App.tsx), so a network hiccup on this one endpoint must
 * not collapse their own nav. Only a settled, successful response with the
 * capability genuinely absent hides the item.
 */
export function hasCapability(ctx: AdminCapabilityContextValue, capability: string | null): boolean {
  if (capability === null) return true;
  if (ctx.loading || ctx.error) return true;
  return ctx.capabilities.includes(capability);
}
